const ASPECT_RATIO: number = 16 / 9;

export class VBrick360Viewer {
	private lat: number;
	private lon: number;

	private animationFrameId: number;
	private camera: THREE.PerspectiveCamera;
	private isCoordsChanged: boolean;
	private isForceUpdate: boolean;
	private isSizeUpdated: boolean;
	private lastRenderMs: number;
	private renderer: THREE.WebGLRenderer;
	private scene: THREE.Scene;
	private three: typeof import('three');
	private texture: THREE.VideoTexture;

	private readonly animationThrottleMs: number = 1000 / 30; // 30 fps
	private readonly distance: number = 0.1;

	constructor(
		private container: HTMLElement,
		private source: HTMLVideoElement
	) { }

	public get currentCoordinates() {
		return {
			lat: this.lat,
			lon: this.lon
		};
	}

	public get domElement(): HTMLCanvasElement {
		return this.renderer.domElement;
	}

	public get isCentered(): boolean {
		return !this.lat && !this.lon;
	}

	public centralize(): void {
		this.setCoordinates(0, 0);
	}

	public destroy(): void {
		window.cancelAnimationFrame(this.animationFrameId);
		this.renderer.domElement.remove();
		this.renderer.dispose();
		this.camera = this.scene = this.renderer = null;
	}

	public forceUpdate(): void {
		this.isForceUpdate = true;
	}

	public render(): Promise<void> {
		return import(/* webpackChunkName: "three.js" */ 'three')
			.then( three => {
				this.three = three; // webpack 4 change this behavior, but TS hasn't followed

				this.camera = new three.PerspectiveCamera(68, ASPECT_RATIO);
				this.centralize();

				const geometry: THREE.SphereBufferGeometry = new three.SphereBufferGeometry(500, 60, 40);
				geometry.scale(-1, 1, 1); // invert the geometry on the x-axis so that all of the faces point inward

				this.texture = new three.VideoTexture(this.source);
				this.texture.minFilter = three.LinearFilter;
				this.texture.format = three.RGBFormat;

				const material: THREE.MeshBasicMaterial = new three.MeshBasicMaterial({
					depthTest: false,
					depthWrite: false, // essential for IE support. Leaving depth test disabled. We don't need it. and could cause perfomance issues on older gpus.
					map: this.texture
				});

				this.scene = new three.Scene();
				this.scene.add(new three.Mesh(geometry, material));

				this.renderer = new three.WebGLRenderer({ antialias: true, alpha: true });
				this.renderer.setPixelRatio(window.devicePixelRatio);

				this.renderer.domElement.classList.add('canvas');
				this.container.appendChild(this.renderer.domElement);

				this.animate(null);
			});
	}

	public setCoordinates(lat: number, lon: number): void {
		lat = Math.max( -85, Math.min( 85, lat));

		// short-circuit recalculations if position is unchanged
		if (this.lat === lat && this.lon === lon) {
			return;
		}

		this.lat = lat;
		this.lon = lon;

		const theta: number = this.three.MathUtils.degToRad(lon);
		const phi: number = this.three.MathUtils.degToRad(90 - lat);
		const sinPhiByDistance: number = this.distance * Math.sin(phi);

		this.camera.position.set(
			sinPhiByDistance * Math.cos(theta), // x
			this.distance * Math.cos(phi), // y
			sinPhiByDistance * Math.sin(theta) // z
		);
		this.camera.lookAt(new this.three.Vector3());

		this.isCoordsChanged = true;
	}

	public setSize(width: number, height: number): void {
		const currentRendererSize = this.renderer.getSize(new this.three.Vector2());

		// short-circuit if size unchanged
		if (currentRendererSize.width === width && currentRendererSize.height === height) {
			return;
		}

		this.camera.aspect = width / height;
		this.camera.updateProjectionMatrix();

		this.renderer.setSize(width, height);

		this.isSizeUpdated = true;
	}

	public update(): void {
		// short-circuit if don't even have current data
		if (this.source.readyState < 2) {
			return;
		}

		// render only when appropriate
		if (this.isCoordsChanged || this.isSizeUpdated || !this.source.paused || this.isForceUpdate) {
			this.renderer.render(this.scene, this.camera);

			this.isCoordsChanged = this.isSizeUpdated = this.isForceUpdate = false;
		}
	}

	private animate(time: number): void {
		this.animationFrameId = window.requestAnimationFrame(time => this.animate(time));
		this.lastRenderMs = time;
		this.update();
	}
}
